import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Globe, Users, Check, Loader2, ArrowLeft, Ticket } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { Loader, PassionBadge, PrimaryButton, GhostButton, money, niceDate } from "@/components/ui-bits";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/events/${id}`).then((r) => setE(r.data));
  useEffect(() => { setE(null); load(); }, [id]);

  const toggle = async () => {
    if (!e.is_attending && e.price > 0) { startCheckout("event", id); return; }
    setBusy(true);
    try {
      if (e.is_attending) { await api.post(`/events/${id}/leave`); toast("You're no longer attending"); }
      else { await api.post(`/events/${id}/join`); toast.success("You're going! 🎉"); }
      await load();
    } catch { toast.error("Action failed"); } finally { setBusy(false); }
  };

  if (!e) return <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"><Loader /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-[#55708F] hover:text-[#FF6B00] mb-5" data-testid="back-to-events"><ArrowLeft className="h-4 w-4" />All events</Link>
      <div className="rounded-3xl overflow-hidden h-64 sm:h-80 relative">
        <img src={fileUrl(e.cover)} alt={e.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/70 to-transparent" />
        <span className="absolute top-4 left-4 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/90 backdrop-blur capitalize">{e.mode}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2">
          <PassionBadge slug={e.passion} />
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#071126] mt-3">{e.title}</h1>
          <p className="text-[#071126]/70 leading-relaxed mt-4 whitespace-pre-wrap">{e.description}</p>
          <div className="flex items-center gap-3 mt-6">
            <img src={fileUrl(e.organizer?.avatar)} alt="" className="h-11 w-11 rounded-full object-cover" />
            <div><p className="text-xs text-[#55708F]">Organized by</p><Link to={`/u/${e.organizer?.id}`} className="font-semibold text-[#071126] hover:text-[#FF6B00]">{e.organizer?.name}</Link></div>
          </div>
          {e.attendee_avatars?.length > 0 && (
            <div className="mt-6"><p className="text-sm font-semibold text-[#071126] mb-2">Who's going</p>
              <div className="flex -space-x-2">{e.attendee_avatars.map((a) => <img key={a.id} src={fileUrl(a.avatar)} alt={a.name} title={a.name} className="h-9 w-9 rounded-full object-cover border-2 border-white" />)}
                {e.attendee_count > e.attendee_avatars.length && <span className="h-9 w-9 rounded-full bg-[#071126]/8 grid place-items-center text-xs font-semibold text-[#071126]/60 border-2 border-white">+{e.attendee_count - e.attendee_avatars.length}</span>}</div>
            </div>
          )}
        </div>

        <div>
          <div className="pa-card p-6 sticky top-20">
            <p className="text-3xl font-extrabold text-[#FF6B00]">{money(e.price)}</p>
            <div className="space-y-3 mt-5 text-sm">
              <Detail icon={Calendar} label={niceDate(e.date)} />
              {e.start_time && <Detail icon={Clock} label={`${e.start_time}${e.end_time ? ` – ${e.end_time}` : ""}`} />}
              <Detail icon={e.mode === "online" ? Globe : MapPin} label={e.location} />
              <Detail icon={Users} label={`${e.attendee_count} / ${e.capacity} going`} />
            </div>
            <div className="mt-6">
              {e.is_attending ? (
                <GhostButton onClick={toggle} disabled={busy} className="w-full" data-testid="leave-event-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" />You're going</>}</GhostButton>
              ) : (
                <PrimaryButton onClick={toggle} disabled={busy} className="w-full" data-testid="join-event-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Ticket className="h-4 w-4" />{e.price > 0 ? "Register" : "Join event"}</>}</PrimaryButton>
              )}
              {e.price > 0 && <p className="text-xs text-center text-[#55708F] mt-2">Secure Stripe checkout · test card 4242 4242 4242 4242</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label }) {
  return <div className="flex items-center gap-3 text-[#071126]/75"><Icon className="h-4.5 w-4.5 text-[#55708F] flex-shrink-0" /><span>{label}</span></div>;
}
